import * as React from 'react'
import {StructureNode} from '../../../commons/types/Structure'
import {getScrollableParent} from '../../../commons/utils/getScrollableParent'
import {DocsetStructure} from '../../store/kv.types'
import {getHitsFromDocsetStructure, Hit} from './getHitsFromStructure'

export type GoToPara = (hit: Hit) => void

interface Result {
  goToNext: () => void
  goToPrevious: () => void
}

type NavigationDirection = 'NEXT' | 'PREVIOUS' | 'UNKNOWN'

const getMarks = (contentRef: React.RefObject<HTMLElement> | null, paraId?: string): NodeListOf<HTMLElement> | null => {
  if (!contentRef?.current) {
    return null
  }
  if (paraId && paraId.length > 0) {
    return contentRef.current.querySelectorAll('.' + CSS.escape(paraId) + ' mark') as NodeListOf<HTMLElement>
  }
  return null
}

const findNextHit = (hits: Hit[], paraId?: string): Hit | null => {
  if (hits.length === 0 || !paraId) {
    return null
  }

  // index of the first hit matching the current para
  const currentIndex = hits.findIndex((h) => h.paraId === paraId)
  if (currentIndex === -1) {
    return null // paraId not found in hits
  }

  // move forward until we reach a different paragraph or run out of hits
  let nextIndex = currentIndex + 1
  while (nextIndex < hits.length && hits[nextIndex].paraId === paraId) {
    nextIndex++
  }

  return nextIndex < hits.length ? hits[nextIndex] : null
}

const findPreviousHit = (hits: Hit[], paraId?: string): Hit | null => {
  if (hits.length === 0 || !paraId) {
    return null
  }

  // index of the *last* hit matching the current paragraph
  let currentIndex = -1
  for (let i = hits.length - 1; i >= 0; i--) {
    if (hits[i].paraId === paraId) {
      currentIndex = i
      break
    }
  }
  if (currentIndex === -1) {
    return null // paraId not found in hits
  }

  // step backward until we reach a different paragraph or run out of hits
  let prevIndex = currentIndex - 1
  while (prevIndex >= 0 && hits[prevIndex].paraId === paraId) {
    prevIndex--
  }

  return prevIndex >= 0 ? hits[prevIndex] : null
}

const findNextMark = (
  marks: NodeListOf<HTMLElement> | null,
  selectedMarkRef: React.MutableRefObject<HTMLElement | null>
): HTMLElement | null => {
  let currentMarkPos = 0
  if (marks) {
    for (let i = 0; i < marks.length; i++) {
      const eachMark = marks[i]
      if (eachMark === selectedMarkRef.current) {
        currentMarkPos = i
        break
      }
    }
    const nextMarkPos = currentMarkPos + 1
    if (marks.length > nextMarkPos) {
      return marks[nextMarkPos]
    }
  }
  return null
}

const findPreviousMark = (
  marks: NodeListOf<HTMLElement> | null,
  selectedMarkRef: React.MutableRefObject<HTMLElement | null>
): HTMLElement | null => {
  let currentMarkPos = -1

  if (marks) {
    // Locate the index of the currently selected mark
    for (let i = 0; i < marks.length; i++) {
      if (marks[i] === selectedMarkRef.current) {
        currentMarkPos = i
        break
      }
    }

    // Step one position to the left
    const previousMarkPos = currentMarkPos - 1

    // Return the element if the index is in range
    if (previousMarkPos >= 0) {
      return marks[previousMarkPos]
    }
  }

  return null
}

export function useSearchNavigation(
  structure: DocsetStructure | StructureNode | null,
  contentRef: React.RefObject<HTMLElement> | null,
  paraId: string | undefined,
  goToPara: GoToPara,
  setNext: (value: boolean) => void,
  setPrevious: (value: boolean) => void
): Result {
  const selectedMarkRef = React.useRef<HTMLElement | null>(null)
  const navigationDirectionRef = React.useRef<NavigationDirection>('UNKNOWN')

  const hits = React.useMemo(() => getHitsFromDocsetStructure(structure), [structure])

  const updateNavigation = React.useCallback(
    (marks: NodeListOf<HTMLElement> | null) => {
      const nextMark = findNextMark(marks, selectedMarkRef)
      const nextHit = findNextHit(hits, paraId)
      setNext(nextHit !== null || nextMark !== null)

      const previousMark = findPreviousMark(marks, selectedMarkRef)
      const previousHit = findPreviousHit(hits, paraId)
      setPrevious(previousHit !== null || previousMark !== null)
    },
    [hits, paraId, setNext, setPrevious]
  )

  const selectMark = React.useCallback(
    (
      marks: NodeListOf<HTMLElement>,
      mark: HTMLElement,
      selectedMarkRef: React.MutableRefObject<HTMLElement | null>
    ) => {
      marks.forEach((mark) => (mark.style.backgroundColor = 'yellow'))
      mark.style.backgroundColor = 'orange'
      mark.scrollIntoView()
      getScrollableParent(mark).scrollBy({top: -10})
      selectedMarkRef.current = mark

      updateNavigation(marks)
    },
    [updateNavigation]
  )

  React.useEffect(() => {
    if (!contentRef?.current) {
      return
    }

    const marks = getMarks(contentRef, paraId)
    if (marks && marks.length !== 0) {
      selectedMarkRef.current = navigationDirectionRef.current === 'PREVIOUS' ? marks[marks.length - 1] : marks[0]
      selectMark(marks, selectedMarkRef.current, selectedMarkRef)
      return
    }

    if (hits && hits.length > 0) {
      updateNavigation(marks)
    }
  }, [contentRef, selectedMarkRef, paraId, selectMark, updateNavigation, hits])

  const goToNext = () => {
    navigationDirectionRef.current = 'NEXT'

    const marks = getMarks(contentRef, paraId)
    const nextMark = findNextMark(marks, selectedMarkRef)
    if (marks && nextMark) {
      selectMark(marks, nextMark, selectedMarkRef)
      return
    }

    const nextHit = findNextHit(hits, paraId)
    if (nextHit) {
      goToPara(nextHit)
    }
  }

  const goToPrevious = () => {
    navigationDirectionRef.current = 'PREVIOUS'

    const marks = getMarks(contentRef, paraId)
    const previousMark = findPreviousMark(marks, selectedMarkRef)
    if (marks && previousMark) {
      selectMark(marks, previousMark, selectedMarkRef)
      return
    }

    const previousHit = findPreviousHit(hits, paraId)
    if (previousHit) {
      goToPara(previousHit)
    }
  }

  return {
    goToNext,
    goToPrevious,
  }
}
